import React, { ReactNode, useRef } from 'react';
import { Button } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import { useSelector } from 'react-redux';
import { CHARCOAL_GREY, GREY } from '~/Constants/Colors.constants';
import { ChevronRight } from '@material-ui/icons';
import { selectBookingGroupList } from '~/store/payments';
import { BookingGroupSelectionModal } from '..';
import { IBookingGroup } from '~/Models/DTO/BookingGroup.model';
import { useBoolean } from '~/Hooks';

interface BookingGroupSelectionInputProps {
  selectedBookingGroupIds: string[];
  children: ReactNode;
  updateBookingGroupList: (uuids: string[]) => void;
}

const useBookingGroupSelectionModal = () => {
  // State
  const { state: isOpen, setTrue: setOpen, setFalse: setClose } = useBoolean(false);

  // Selectors
  const bookingGroupsList = useSelector(selectBookingGroupList);

  return {
    isOpen,
    setOpen,
    setClose,
    bookingGroupsList,
  };
};

const useStyles = (bookingGroupSelected: boolean) =>
  makeStyles(() => ({
    bookingGroupSelectionInputWrapper: {
      width: '100%',
      display: 'flex',
      overflowWrap: 'anywhere',
    },
    button: {
      color: bookingGroupSelected ? CHARCOAL_GREY : GREY,
      fontWeight: 'normal',
      marginLeft: 'auto',
      paddingRight: 0,
      // Offsets Chevron padding
      marginRight: '-8px',
    },
    ChevronRight: {
      color: GREY,
      display: 'inherit',
      alignItems: 'inherit',
      justifyContent: 'inherit',
    },
  }));

const getSelectedBookingGroupIds = (selectedBookingGroupIds: string[], groups: IBookingGroup[]) =>
  groups
    ?.filter(({ uuid }) => selectedBookingGroupIds.includes(uuid))
    ?.map(({ name }) => name)
    ?.join(', ');

export const BookingGroupSelectionInput = ({
  children,
  selectedBookingGroupIds,
  updateBookingGroupList,
}: BookingGroupSelectionInputProps) => {
  const { classes } = useStyles(!!selectedBookingGroupIds?.length)();
  const { isOpen, setOpen, setClose, bookingGroupsList } = useBookingGroupSelectionModal();

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      className={classes.bookingGroupSelectionInputWrapper}
      data-testid="bookinggroupselectioninput-wrapper"
    >
      <Button
        type="button"
        className={classes.button}
        onClick={setOpen}
        data-testid="bookinggroupselectioninput-button"
        ref={buttonRef}
      >
        {selectedBookingGroupIds
          ? getSelectedBookingGroupIds(selectedBookingGroupIds, bookingGroupsList)
          : children}
        <span className={classes.ChevronRight}>
          <ChevronRight />
        </span>
      </Button>
      <BookingGroupSelectionModal
        open={isOpen}
        onClose={setClose}
        updateBookingGroupList={updateBookingGroupList}
        selectedBookingGroupIds={selectedBookingGroupIds}
        data-testid="bookinggroupselectioninput-modal"
        anchorEl={buttonRef.current}
      />
    </div>
  );
};
